/**
 * 工具函数模块
 * 
 * 提供 cn() 函数用于合并 Tailwind CSS 类名
 * 使用 clsx 处理条件类名，tailwind-merge 解决类名冲突
 */
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * 合并 CSS 类名
 * 
 * 功能说明：
 * - 使用 clsx 处理条件类名（支持字符串、对象、数组等多种格式）
 * - 使用 tailwind-merge 解决 Tailwind 类名冲突（后者覆盖前者）
 * 
 * @example
 * // 基础用法
 * cn('px-4 py-2', 'bg-blue-500') // 'px-4 py-2 bg-blue-500'
 * 
 * @example
 * // 条件类名
 * cn('base-class', isActive && 'active-class') // 'base-class active-class' (当 isActive 为 true)
 * 
 * @example
 * // 类名冲突解决
 * cn('px-4', 'px-2') // 'px-2' (后者覆盖前者)
 * 
 * @example
 * // 对象语法
 * cn({ 'bg-red-500': hasError, 'bg-green-500': isSuccess }) // 根据条件返回对应类名
 * 
 * @param inputs - 类名输入，支持多种格式
 * @returns 合并后的类名字符串
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/**
 * 格式化文件大小
 * 
 * @param bytes - 字节数
 * @param decimals - 小数位数，默认 2
 * @returns 格式化后的文件大小字符串
 */
export function formatFileSize(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

/**
 * 延迟执行函数
 * 
 * @param ms - 延迟毫秒数
 * @returns Promise，在指定时间后 resolve
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * 生成唯一 ID
 * 
 * @param prefix - ID 前缀，默认为空
 * @returns 唯一 ID 字符串
 */
export function generateId(prefix: string = ''): string {
  const timestamp = Date.now().toString(36)
  const randomStr = Math.random().toString(36).substring(2, 8)
  return prefix ? `${prefix}_${timestamp}_${randomStr}` : `${timestamp}_${randomStr}`
}

/**
 * 防抖函数
 * 
 * @param fn - 需要防抖的函数
 * @param delay - 延迟毫秒数
 * @returns 防抖后的函数
 */
export function debounce<T extends (...args: Parameters<T>) => ReturnType<T>>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  return function (this: unknown, ...args: Parameters<T>) {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    timeoutId = setTimeout(() => {
      fn.apply(this, args)
    }, delay)
  }
}

/**
 * 格式化金额
 * 
 * 去掉货币符号，金额后加空格+币种
 * 
 * @example
 * formatMoney(100)       // '100.00 CNY'
 * formatMoney('99.9')    // '99.90 CNY'
 * formatMoney(50, 'USD') // '50.00 USD'
 * 
 * @param amount - 金额数值
 * @param currency - 币种代码，默认 CNY
 * @returns 格式化后的金额字符串
 */
export function formatMoney(amount: number | string | undefined, currency: string = 'CNY'): string {
  const num = amount === undefined || amount === null ? NaN : (typeof amount === 'string' ? parseFloat(amount) : amount)
  if (isNaN(num)) return '-'
  return `${num.toFixed(2)} ${currency}`
}

/**
 * 节流函数
 * 
 * @param fn - 需要节流的函数
 * @param limit - 时间限制（毫秒）
 * @returns 节流后的函数
 */
export function throttle<T extends (...args: Parameters<T>) => ReturnType<T>>(
  fn: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false

  return function (this: unknown, ...args: Parameters<T>) {
    if (!inThrottle) {
      fn.apply(this, args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}